import React, { useEffect, useRef, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import SidebarLinkGroup from './SidebarLinkGroup';
import Logo from '../../images/Logo-Sidebar.svg';
import LinesBg from '../../images/lines-background-2.svg'; // add import
import { useGlobalState } from '../../state';
import { FaHome, FaUpload, FaMoneyCheckAlt, FaMapMarkedAlt, FaGift, FaPhone, FaPiggyBank, FaIdCard, FaFile } from 'react-icons/fa';
import { jwtDecode } from 'jwt-decode'; // Import jwt-decode
import packageJson from '../../../package.json';

interface SidebarProps {
    sidebarOpen: boolean;
    setSidebarOpen: (arg: boolean) => void;
    afiliadosOpen: boolean;
    setAfiliadosOpen: (arg: boolean) => void;
    creditosOpen: boolean
    setCreditosOpen: (arg: boolean) => void;
}

const Sidebar = ({ sidebarOpen, setSidebarOpen, afiliadosOpen, setAfiliadosOpen, creditosOpen, setCreditosOpen }: SidebarProps) => {

    const [user, setUser] = useGlobalState('user')   
    const [userRoles, setUserRoles] = useState<string[] | string>([]);

    const handleAfiliadosClick = () => setAfiliadosOpen(!afiliadosOpen);
    const handleCreditosClick = () => setCreditosOpen(!creditosOpen);

    const location = useLocation();
    const { pathname } = location;

    const trigger = useRef<any>(null);
    const sidebar = useRef<any>(null);

    const storedSidebarExpanded = localStorage.getItem('sidebar-expanded');
    const [sidebarExpanded, setSidebarExpanded] = useState(
        storedSidebarExpanded === null ? false : storedSidebarExpanded === 'true'
    );

    // close on click outside
    useEffect(() => {
        const clickHandler = ({ target }: MouseEvent) => {
            if (!sidebar.current || !trigger.current) return;
            if (
                !sidebarOpen ||
                sidebar.current.contains(target) ||
                trigger.current.contains(target)
            )
                return;
            setSidebarOpen(false);
        };
        document.addEventListener('click', clickHandler);
        return () => document.removeEventListener('click', clickHandler);
    });

    // close if the esc key is pressed
    useEffect(() => {
        const keyHandler = ({ keyCode }: KeyboardEvent) => {
            if (!sidebarOpen || keyCode !== 27) return;
            setSidebarOpen(false);
        };
        document.addEventListener('keydown', keyHandler);
        return () => document.removeEventListener('keydown', keyHandler);
    });

    useEffect(() => {
        localStorage.setItem('sidebar-expanded', sidebarExpanded.toString());
        if (sidebarExpanded) {
            document.querySelector('body')?.classList.add('sidebar-expanded');
        } else {
            document.querySelector('body')?.classList.remove('sidebar-expanded');
        }
    }, [sidebarExpanded]);

    useEffect(() => {
        if (!user)
            setSidebarOpen(false);
        try {
            const decodedToken = jwtDecode(user.accessToken);
            // @ts-ignore
            const roles = decodedToken["http://schemas.microsoft.com/ws/2008/06/identity/claims/role"] || [];
       
            setUserRoles(roles);
        } catch (error) {
            console.error('Error decoding token:', error);
        }
    }, []);

    // Check if the user has a specific role
    const hasRole = (role: string) => {
        return userRoles.includes(role);
    }

    return (
        <aside
            ref={sidebar}
            className={`absolute left-0 top-0 z-9999 flex h-screen w-72.5 flex-col overflow-y-hidden bg-primario-azul-900 duration-300 ease-linear dark:bg-boxdark lg:static lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'
                }`}
            style={{
                backgroundImage: `linear-gradient(rgba(29, 34, 51, 0.8), rgba(29, 34, 51, 0.8)), url(${LinesBg})`,
                backgroundRepeat: 'no-repeat',
                backgroundSize: 'cover',
                backgroundPosition: 'center',
            }}
        >
            {/* <!-- SIDEBAR HEADER --> */}
            <div className="flex items-center justify-between gap-2 px-6 py-5.5 lg:py-6.5">
                <NavLink to="/">
                    <img src={Logo} alt="Logo" />
                </NavLink>

                <button
                    ref={trigger}
                    onClick={() => setSidebarOpen(!sidebarOpen)}
                    aria-controls="sidebar"
                    aria-expanded={sidebarOpen}
                    className="block lg:hidden"
                >
                    <svg
                        className="fill-current"
                        width="20"
                        height="18"
                        viewBox="0 0 20 18"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <path
                            d="M19 8.175H2.98748L9.36248 1.6875C9.69998 1.35 9.69998 0.825 9.36248 0.4875C9.02498 0.15 8.49998 0.15 8.16248 0.4875L0.399976 8.3625C0.0624756 8.7 0.0624756 9.225 0.399976 9.5625L8.16248 17.4375C8.31248 17.5875 8.53748 17.7 8.76248 17.7C8.98748 17.7 9.17498 17.625 9.36248 17.475C9.69998 17.1375 9.69998 16.6125 9.36248 16.275L3.02498 9.8625H19C19.45 9.8625 19.825 9.4875 19.825 9.0375C19.825 8.55 19.45 8.175 19 8.175Z"
                            fill=""
                        />
                    </svg>
                </button>
            </div>
            {/* <!-- SIDEBAR HEADER --> */}

            <div className="no-scrollbar flex flex-col overflow-y-auto duration-300 ease-linear">
                {/* <!-- Sidebar Menu --> */}
                <nav className="mt-5 py-4 px-4 lg:mt-9 lg:px-6">
                    {/* <!-- Menu Group --> */}
                    <div>
                        <ul className="mb-6 flex flex-col gap-1.5">
                            <li>
                                <NavLink
                                    to="/"
                                    className={`group relative flex items-center gap-2.5 rounded-sm py-2 px-4 font-medium text-bodydark1 duration-300 ease-in-out hover:bg-graydark dark:hover:bg-meta-4 ${pathname.includes('tables') && 'bg-graydark dark:bg-meta-4'}`}
                                >
                                    <FaHome className="text-white text-2xl" />
                                    Inicio
                                </NavLink>
                            </li>
                            { !user?.datosUsuario?.beneficiario &&
                            <li>
                                <NavLink
                                    to="/pagos/boleta"
                                    className={`group relative flex items-center gap-2.5 rounded-sm py-2 px-4 font-medium text-bodydark1 duration-300 ease-in-out hover:bg-graydark dark:hover:bg-meta-4 ${pathname.includes('tables') && 'bg-graydark dark:bg-meta-4'}`}
                                >
                                    <FaMoneyCheckAlt className="text-white text-2xl" />
                                    Pagar boleta
                                </NavLink>
                                </li>
                            }

                            {!user?.datosUsuario?.beneficiario &&
                                <li>
                                    <NavLink
                                        to="/pagos/transferencia"
                                        className={`group relative flex items-center gap-2.5 rounded-sm py-2 px-4 font-medium text-bodydark1 duration-300 ease-in-out hover:bg-graydark dark:hover:bg-meta-4 ${pathname.includes('tables') && 'bg-graydark dark:bg-meta-4'}`}
                                    >
                                        <FaUpload className="text-white text-2xl" />
                                        Informar transferencia
                                    </NavLink>
                                </li>
                            }

                            <li>
                                <NavLink
                                    to="/ColegiosDepartamentales"
                                    className={`group relative flex items-center gap-2.5 rounded-sm py-2 px-4 font-medium text-bodydark1 duration-300 ease-in-out hover:bg-graydark dark:hover:bg-meta-4 ${pathname.includes('tables') && 'bg-graydark dark:bg-meta-4'}`}
                                >
                                    <FaMapMarkedAlt className="text-white text-2xl" />
                                    Colegios departamentales
                                </NavLink>
                            </li>

                           {/* {user?.datosUsuario?.beneficiario &&*/}
                                <li>
                                    <NavLink
                                        to="/prestamos"
                                        className={`group relative flex items-center gap-2.5 rounded-sm py-2 px-4 font-medium text-bodydark1 duration-300 ease-in-out hover:bg-graydark dark:hover:bg-meta-4 ${pathname.includes('tables') && 'bg-graydark dark:bg-meta-4'}`}
                                    >
                                        <FaPiggyBank className="text-white text-2xl" />
                                        Préstamos
                                    </NavLink>
                                </li>
                           {/* }*/}

                            {user?.datosUsuario?.beneficiario &&
                                <li>
                                    <NavLink
                                        to="/supervivencia"
                                        className={`group relative flex items-center gap-2.5 rounded-sm py-2 px-4 font-medium text-bodydark1 duration-300 ease-in-out hover:bg-graydark dark:hover:bg-meta-4 ${pathname.includes('tables') && 'bg-graydark dark:bg-meta-4'}`}
                                    >
                                        <FaIdCard className="text-white text-2xl" />
                                        Supervivencia
                                    </NavLink>
                                </li>
                            }

                            {user?.datosUsuario?.beneficiario &&
                            <li>
                                <NavLink
                                    to="/recibos"
                                    className={`group relative flex items-center gap-2.5 rounded-sm py-2 px-4 font-medium text-bodydark1 duration-300 ease-in-out hover:bg-graydark dark:hover:bg-meta-4 ${pathname.includes('tables') && 'bg-graydark dark:bg-meta-4'}`}
                                >
                                    <FaFile className="text-white text-2xl" />
                                    Recibos de Haberes
                                </NavLink>
                            </li>
                            }

                            <li>
                                <NavLink
                                    to="/beneficios"
                                    className={`group relative flex items-center gap-2.5 rounded-sm py-2 px-4 font-medium text-bodydark1 duration-300 ease-in-out hover:bg-graydark dark:hover:bg-meta-4 ${pathname.includes('tables') && 'bg-graydark dark:bg-meta-4'}`}
                                >
                                    <FaGift className="text-white text-2xl" />
                                    Beneficios
                                </NavLink>
                            </li>

                            <li>
                                <NavLink
                                    to="/contacto"
                                    className={`group relative flex items-center gap-2.5 rounded-sm py-2 px-4 font-medium text-bodydark1 duration-300 ease-in-out hover:bg-graydark dark:hover:bg-meta-4 ${pathname.includes('tables') && 'bg-graydark dark:bg-meta-4'}`}
                                >
                                    <FaPhone className="text-white text-2xl" />
                                    Contacto
                                </NavLink>
                            </li>
                        </ul>
                            
                                      
                    </div>
                </nav>
                {/* <!-- Sidebar Menu --> */}
            </div>

            {/* Footer con versión */}
            <div className="p-4 text-center text-xs text-white mt-[150px]">
                v{packageJson.version}
            </div>
        </aside>
    );
};

export default Sidebar;
